import { type Location, useLocation } from '@remix-run/react'
import React, {
	createContext,
	useContext,
	useState,
	useEffect,
	type ReactNode,
} from 'react'

// Define the type for our context value
type NavigationHistoryContextType = Location[]

// Create a context with an empty array as the default value
const NavigationHistoryContext = createContext<NavigationHistoryContextType>([])

// Props type for the provider component
type NavigationHistoryProviderProps = {
	children: ReactNode
}

// Context provider component
export const NavigationHistoryProvider: React.FC<
	NavigationHistoryProviderProps
> = ({ children }) => {
	const [history, setHistory] = useState<NavigationHistoryContextType>([])
	const location = useLocation()

	useEffect(() => {
		// Add the new location to the history array
		setHistory(prevHistory => {
			// only add the location if it's not the same as the last one
			if (
				prevHistory.length === 0 ||
				prevHistory[prevHistory.length - 1].pathname !== location.pathname
			) {
				return [...prevHistory, location]
			}
			return prevHistory
		})
	}, [location])

	return (
		<NavigationHistoryContext.Provider value={history}>
			{children}
		</NavigationHistoryContext.Provider>
	)
}

// Hook to use navigation history
export const useNavigationHistory = () => useContext(NavigationHistoryContext)
